import React, { useState } from 'react';
import { X, Send, MessageSquare } from 'lucide-react';

interface SuggestionBoxProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SuggestionBox({ isOpen, onClose }: SuggestionBoxProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [suggestion, setSuggestion] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Import dependencies
      const { Database } = await import('@/database/Database');
      const { sendEmail } = await import('@/lib/email');

      // Save to database
      const db = new Database();
      
      await db.query(
        'INSERT INTO entities (entity_type, data, created_at, updated_at) VALUES (?, ?, ?, ?)',
        [
          'Suggestion',
          JSON.stringify({
            name,
            email,
            suggestion,
            read: false,
          }),
          new Date().toISOString(),
          new Date().toISOString(),
        ]
      );

      // Send email to admin
      const emailHtml = `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h2 style="color: #f59e0b; border-bottom: 2px solid #f59e0b; padding-bottom: 10px;">
            New Suggestion from WHIPT
          </h2>
          
          <div style="background: #f3f4f6; padding: 20px; border-radius: 8px; margin: 20px 0;">
            <p style="margin: 10px 0;"><strong>From:</strong> ${name}</p>
            <p style="margin: 10px 0;"><strong>Email:</strong> ${email}</p>
            <p style="margin: 10px 0;"><strong>Submitted:</strong> ${new Date().toLocaleString()}</p>
          </div>
          
          <h3 style="color: #374151; margin-top: 30px;">Suggestion:</h3>
          <div style="background: #ffffff; padding: 20px; border-left: 4px solid #f59e0b; margin: 10px 0;">
            <p style="white-space: pre-wrap; line-height: 1.6;">${suggestion}</p>
          </div>
          
          <hr style="border: none; border-top: 1px solid #e5e7eb; margin: 30px 0;" />
          
          <p style="color: #6b7280; font-size: 12px; text-align: center;">
            This suggestion was submitted through the WHIPT Suggestion Box
          </p>
        </div>
      `;

      await sendEmail({
        to: 'contact@theblackbettermentquest.org',
        from: import.meta.env.VITE_FROM_EMAIL || 'noreply@whipt.app',
        subject: `WHIPT Suggestion from ${name}`,
        html: emailHtml,
        text: `New Suggestion from WHIPT\n\nFrom: ${name}\nEmail: ${email}\nSubmitted: ${new Date().toLocaleString()}\n\nSuggestion:\n${suggestion}`,
        replyTo: email,
      });

      // Send confirmation to user
      const confirmationHtml = `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h2 style="color: #f59e0b;">Thank You for Your Suggestion!</h2>
          <p>Hi ${name},</p>
          <p>We've received your suggestion and appreciate you taking the time to share your feedback with us.</p>
          
          <div style="background: #f3f4f6; padding: 20px; border-radius: 8px; margin: 20px 0;">
            <p style="margin: 0;"><strong>Your suggestion:</strong></p>
            <p style="margin: 10px 0 0 0; white-space: pre-wrap;">${suggestion}</p>
          </div>
          
          <p>We review all suggestions carefully and use them to improve WHIPT for everyone.</p>
          <p>Best regards,<br />The WHIPT Team</p>
          
          <hr style="border: none; border-top: 1px solid #e5e7eb; margin: 30px 0;" />
          
          <p style="color: #6b7280; font-size: 12px;">
            Questions? Contact us at <a href="mailto:contact@theblackbettermentquest.org" style="color: #f59e0b;">contact@theblackbettermentquest.org</a>
          </p>
        </div>
      `;

      await sendEmail({
        to: email,
        from: import.meta.env.VITE_FROM_EMAIL || 'noreply@whipt.app',
        subject: 'Thank you for your WHIPT suggestion',
        html: confirmationHtml,
        text: `Hi ${name},\n\nThank you for your suggestion! We've received it and will review it carefully.\n\nYour suggestion:\n${suggestion}\n\nBest regards,\nThe WHIPT Team`,
      });

      setSubmitStatus('success');
      // Clear form
      setName('');
      setEmail('');
      setSuggestion('');
      // Close after 2 seconds
      setTimeout(() => {
        onClose();
        setSubmitStatus('idle');
      }, 2000);
    } catch (error) {
      console.error('Error submitting suggestion:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 z-[9998]">
      <div className="relative w-full max-w-lg bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl border-2 border-amber-400/30 shadow-2xl">
        
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4 rounded-t-3xl">
          <div className="flex items-center gap-3">
            <MessageSquare className="w-8 h-8 text-white" />
            <div>
              <h2 className="text-2xl font-bold text-white">Suggestion Box</h2>
              <p className="text-sm text-amber-100">We'd love to hear from you!</p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          
          {/* Name Input */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-slate-300 mb-2">
              Your Name
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              placeholder="John Doe"
            />
          </div>

          {/* Email Input */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-2">
              Your Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              placeholder="john@example.com"
            />
          </div>

          {/* Suggestion Textarea */}
          <div>
            <label htmlFor="suggestion" className="block text-sm font-medium text-slate-300 mb-2">
              Your Suggestion
            </label>
            <textarea
              id="suggestion"
              value={suggestion}
              onChange={(e) => setSuggestion(e.target.value)}
              required
              rows={6}
              className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
              placeholder="Tell us your ideas, feedback, or suggestions..."
            />
          </div>

          {/* Status Messages */}
          {submitStatus === 'success' && (
            <div className="bg-green-500/20 border border-green-500/50 rounded-xl p-4 text-green-300 text-center">
              ✓ Thank you! Your suggestion has been sent.
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="bg-red-500/20 border border-red-500/50 rounded-xl p-4 text-red-300 text-center">
              ✗ Failed to send. Please try again or email us directly at contact@theblackbettermentquest.org
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold py-3 px-6 rounded-xl hover:shadow-lg hover:shadow-amber-500/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Sending...</span>
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                <span>Send Suggestion</span>
              </>
            )}
          </button>

          {/* Contact Info */}
          <p className="text-xs text-slate-400 text-center mt-4">
            Or email us directly at{' '}
            <a 
              href="mailto:contact@theblackbettermentquest.org" 
              className="text-amber-400 hover:text-amber-300 underline"
            >
              contact@theblackbettermentquest.org
            </a>
          </p>
        </form>
      </div>
    </div>
  );
}
